// Come si dice, in italiano, quando parte un'attività e quando è partita l'ultima volta.
import type { RoutineCondition, RoutineSchedule, RoutineTrigger, ToolGroup } from '../../lib/api';

export function describeWhen(
  trigger: RoutineTrigger,
  schedule: RoutineSchedule | null,
  inputHint?: string | null,
): string {
  if (trigger === 'manual') return 'A mano';
  if (trigger === 'hook') return 'Da un hook';
  if (trigger === 'condition') return 'Quando succede';
  if (trigger === 'input') return `Con un input · chiede «${inputHint || 'testo'}»`;
  if (schedule?.daily_at) return `Ogni giorno alle ${schedule.daily_at}`;
  if (schedule?.every_minutes)
    return schedule.every_minutes === 1 ? 'Ogni minuto' : `Ogni ${schedule.every_minutes} minuti`;
  const n = schedule?.every_hours ?? 0;
  return n === 1 ? 'Ogni ora' : `Ogni ${n} ore`;
}

const TIME = new Intl.DateTimeFormat('it-IT', { hour: '2-digit', minute: '2-digit' });
const DAY = new Intl.DateTimeFormat('it-IT', { weekday: 'short', day: 'numeric', month: 'short' });

/** «alle 12:00» oggi, «domani alle 08:00», altrimenti il giorno. */
export function when(iso: string, now = new Date()): string {
  const d = new Date(iso);
  const days = Math.round(
    (new Date(d.toDateString()).getTime() - new Date(now.toDateString()).getTime()) / 86_400_000,
  );
  const time = TIME.format(d);
  if (days === 0) return `oggi alle ${time}`;
  if (days === 1) return `domani alle ${time}`;
  if (days === -1) return `ieri alle ${time}`;
  return `${DAY.format(d)} alle ${time}`;
}

/** La condizione di «Quando succede», detta per lo strumento che la fa partire. */
export function describeCondition(c: RoutineCondition, groups: ToolGroup[]): string {
  const every = c.every_minutes === 1 ? 'ogni minuto' : `ogni ${c.every_minutes} minuti`;
  if (c.source === 'gmail') {
    const name = groups.find((g) => g.id === c.connection)?.name ?? c.connection ?? 'Gmail';
    const what = c.label ? `con l’etichetta «${c.label}»` : `«${c.query || 'in:inbox'}»`;
    const runs = c.batch === 'all' ? 'un giro con tutte' : 'un giro per email';
    return `Email nuove di ${name} ${what}, ${every}, ${runs}`;
  }
  if (c.source === 'calendar') {
    const name = groups.find((g) => g.id === c.connection)?.name ?? c.connection ?? 'Google';
    const what = c.query ? ` con «${c.query}»` : '';
    return `Appuntamenti${what} di ${name}, ${c.minutes_before ?? 15} minuti prima, ${every}`;
  }
  if (c.source === 'api') {
    const name = groups.find((g) => g.id === c.connection)?.name ?? c.connection ?? 'API';
    const op = c.op === 'contains' ? 'contiene' : c.op.replace('>=', '≥').replace('<=', '≤');
    const fire = c.fire === 'always' ? 'a ogni controllo' : 'quando diventa vera';
    return `API ${name}: ${c.field} ${op} ${c.target}, ${every}, ${fire}`;
  }
  const where =
    c.scope === 'all'
      ? 'tutti gli spazi'
      : c.scope === 'space'
        ? `${c.space}, tutti i progetti`
        : c.project
          ? `${c.space}/${c.project}`
          : (c.space ?? 'memo');
  const metric = c.metric === 'anomalies' ? 'anomalie' : 'ticket in backlog';
  return `memo ${where}: ${metric} ${c.op.replace('>=', '≥').replace('<=', '≤')} ${c.value}, ${every}`;
}
